import React from 'react';
import PropTypes from 'prop-types';
import Markdown from 'markdown-to-jsx/react';
import { I18n } from 'react-redux-i18n';
import { NawiMark } from './NawiIdentity';
import { useNawiChatActions, useNawiChatConversation } from './NawiChatContext';

const messagePropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  sender: PropTypes.string.isRequired,
  status: PropTypes.string,
  text: PropTypes.string.isRequired,
  topics: PropTypes.arrayOf(PropTypes.string),
});

// Links open in a new tab and never leak the opener or the referrer.
const MarkdownLink = ({ children, ...props }) => (
  <a {...props} rel="noopener noreferrer" target="_blank">
    {children}
  </a>
);

MarkdownLink.propTypes = {
  children: PropTypes.node,
};

const markdownOptions = {
  disableParsingRawHTML: true,
  forceBlock: true,
  overrides: { a: { component: MarkdownLink } },
};

const CopyIcon = ({ copied }) =>
  copied ? (
    <svg
      aria-hidden="true"
      fill="none"
      height="14"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="14"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ) : (
    <svg
      aria-hidden="true"
      fill="none"
      height="14"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="14"
    >
      <rect height="13" rx="2" width="13" x="9" y="9" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );

CopyIcon.propTypes = {
  copied: PropTypes.bool.isRequired,
};

const ResponseActions = ({ message }) => {
  const { handleCopy } = useNawiChatActions();
  const { copyStatus } = useNawiChatConversation();
  const isCurrentMessage = copyStatus.id === message.id;
  const isCopied = isCurrentMessage && copyStatus.value === 'copied';

  return (
    <div className="resp-actions">
      <span className="resp-model-tag">{I18n.t('persons.nawiChat.model')}</span>
      <button
        className={`resp-action-btn ${isCopied ? 'copied' : ''}`}
        onClick={() => handleCopy(message)}
        title={I18n.t('persons.nawiChat.copy')}
        type="button"
      >
        <CopyIcon copied={isCopied} />
        <span>
          {isCurrentMessage
            ? I18n.t(`persons.nawiChat.${copyStatus.value}`)
            : I18n.t('persons.nawiChat.copy')}
        </span>
      </button>
    </div>
  );
};

ResponseActions.propTypes = {
  message: messagePropType.isRequired,
};

const FollowUpQuestions = ({ topics }) => {
  const { sendMessage } = useNawiChatActions();

  if (!topics?.length) return null;

  return (
    <div className="nawi-chat__follow-up-questions">
      {topics.map((topic, index) => (
        <div className="suggested-question" key={`${index}:${topic}`}>
          <ol start={index + 1}>
            <li>{topic}</li>
          </ol>
          <button
            className="send-question-btn"
            onClick={() => sendMessage(topic)}
            title={I18n.t('persons.nawiChat.askQuestion')}
            type="button"
          >
            <svg
              aria-hidden="true"
              fill="none"
              height="13"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="13"
            >
              <line x1="22" x2="11" y1="2" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <span>{I18n.t('persons.nawiChat.ask')}</span>
          </button>
        </div>
      ))}
    </div>
  );
};

FollowUpQuestions.propTypes = {
  topics: PropTypes.arrayOf(PropTypes.string),
};

const AssistantMessage = ({ message }) => (
  <>
    <div
      className={`nawi-chat__avatar ${
        message.status === 'streaming' && !message.text
          ? 'nawi-chat__avatar--thinking'
          : ''
      }`}
    >
      <NawiMark size={28} />
    </div>
    <div className="nawi-chat__response">
      {message.text ? (
        <div className="nawi-chat__streamed-text nawi-chat__response-body">
          <Markdown options={markdownOptions}>{message.text}</Markdown>
        </div>
      ) : (
        <p className="nawi-chat__thinking">
          {I18n.t('persons.nawiChat.thinking')}
        </p>
      )}
      {message.status === 'incomplete' && (
        <p className="nawi-chat__incomplete-note" role="status">
          {I18n.t('persons.nawiChat.streamError.partialSaved')}
        </p>
      )}
      {message.status !== 'streaming' && message.text && (
        <ResponseActions message={message} />
      )}
      {message.status === 'complete' && (
        <FollowUpQuestions topics={message.topics} />
      )}
    </div>
  </>
);

AssistantMessage.propTypes = {
  message: messagePropType.isRequired,
};

const MessageList = () => {
  const { messages, messagesEndRef } = useNawiChatConversation();

  return (
    <div className="nawi-chat__messages">
      {messages.map((message) => (
        <div
          className={`nawi-chat__message nawi-chat__message--${message.sender}`}
          key={message.id}
        >
          {message.sender === 'assistant' ? (
            <AssistantMessage message={message} />
          ) : (
            <p>{message.text}</p>
          )}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
